const NOOP = (): void => {};

/**
 * Lets only the most recent of several overlapping requests apply its response.
 * Call begin() when a request starts and keep the token; when the response
 * arrives, apply it only if isLatest(token) — an older response that resolves
 * after a newer request was started is stale and must be dropped.
 *
 * A request can also carry follow-up work (onLatest) that has to happen once the
 * list settles, whichever request ends up being the latest. The work is held
 * here rather than with the request, so a stale response does not take it down
 * with it: a newer request started with beginKeepingPending() inherits it, and
 * complete() hands it out exactly once, with the latest response.
 *
 * A request may also observe its own response (onResponse), stale or not. That is
 * for reading what the response says, not for applying it: a request opened on an
 * unfiltered list can learn a fact about a record from it even after a filtered
 * search has taken over the visible list. Only the request that registered the
 * observer runs it, once, and only with a response (not on failure).
 */
export class LatestRequestToken<TResponse = unknown> {
  private current = 0;
  private pending: (() => void) | undefined;
  private readonly observers = new Map<number, (response: TResponse) => void>();

  /**
   * Starts a request. Follow-up work from an earlier request is replaced by onLatest (or dropped when omitted).
   * onResponse sees this request's own response even when it has gone stale.
   */
  begin(onLatest?: () => void, onResponse?: (response: TResponse) => void): number {
    this.current += 1;
    this.pending = onLatest;
    return this.observe(onResponse);
  }

  /** Starts a request that supersedes the earlier ones but inherits their follow-up work. */
  beginKeepingPending(onResponse?: (response: TResponse) => void): number {
    this.current += 1;
    return this.observe(onResponse);
  }

  private observe(onResponse?: (response: TResponse) => void): number {
    if (onResponse) this.observers.set(this.current, onResponse);
    return this.current;
  }

  isLatest(token: number): boolean {
    return token === this.current;
  }

  /**
   * Called when a response arrives. Returns null for a stale response (drop it);
   * for the latest one, returns the follow-up work to run after applying it
   * (a no-op when there is none) and clears it.
   */
  complete(token: number): (() => void) | null {
    this.observers.delete(token);
    if (!this.isLatest(token)) return null;
    const work = this.pending ?? NOOP;
    this.pending = undefined;
    return work;
  }

  /**
   * Called when a response arrives with its data. Gives the request's own observer the data first,
   * stale or not, then completes as complete() does. An observer that starts a newer request makes
   * this response stale, so complete() then returns null and the caller drops it.
   */
  completeWith(token: number, response: TResponse): (() => void) | null {
    const observer = this.observers.get(token);
    this.observers.delete(token);
    observer?.(response);
    return this.complete(token);
  }
}
